import i18n from "./i18n.ts";

const ownerMessageFrom = "The Owner";
const annualPerformanceEvaluation = "Annual performance evaluation";
const seasonExpectations = "Season expectations";
// N4-3 in-season owner feedback subjects.
const pulseSubject = "Season update";
const milestoneClinchedSubject = "Playoff berth clinched";
const milestoneEliminatedSubject = "Eliminated from playoff contention";
const freeAgencySummarySubject = "Free agency recap";

const translateMood = (value: string) => {
	switch (value) {
		case "Horrible!":
			return i18n.t("ownerMessage.moodHorrible", {
				ns: "ui",
				defaultValue: "Horrible!",
			});
		case "Bad.":
			return i18n.t("ownerMessage.moodBad", {
				ns: "ui",
				defaultValue: "Bad.",
			});
		case "Pretty good.":
			return i18n.t("ownerMessage.moodPrettyGood", {
				ns: "ui",
				defaultValue: "Pretty good.",
			});
		case "Good.":
			return i18n.t("ownerMessage.moodGood", {
				ns: "ui",
				defaultValue: "Good.",
			});
		case "Excellent!":
			return i18n.t("ownerMessage.moodExcellent", {
				ns: "ui",
				defaultValue: "Excellent!",
			});
		default:
			return value;
	}
};

const translateOverall = (value: string) => {
	if (value === "It's too early to judge you.") {
		return i18n.t("ownerMessage.tooEarly", {
			ns: "ui",
			defaultValue: "It's too early to judge you.",
		});
	}

	return translateMood(value);
};

const exactTranslations = [
	{
		source: "Keep it up.",
		translate: () =>
			i18n.t("ownerMessage.keepItUp", {
				ns: "ui",
				defaultValue: "Keep it up.",
			}),
	},
	{
		source: "Keep it up on the court, but I need more money.",
		translate: () =>
			i18n.t("ownerMessage.keepItUpMoney", {
				ns: "ui",
				defaultValue: "Keep it up on the court, but I need more money.",
			}),
	},
	{
		source: "Hopefully you'll win some more games next season.",
		translate: () =>
			i18n.t("ownerMessage.winMoreNextSeason", {
				ns: "ui",
				defaultValue: "Hopefully you'll win some more games next season.",
			}),
	},
	{
		source: "And try to make some more money too!",
		translate: () =>
			i18n.t("ownerMessage.makeMoreMoneyToo", {
				ns: "ui",
				defaultValue: "And try to make some more money too!",
			}),
	},
	{
		source: "Somehow you need to win more and make more money.",
		translate: () =>
			i18n.t("ownerMessage.winAndProfit", {
				ns: "ui",
				defaultValue: "Somehow you need to win more and make more money.",
			}),
	},
	{
		source: "Win some more games next season.",
		translate: () =>
			i18n.t("ownerMessage.winMore", {
				ns: "ui",
				defaultValue: "Win some more games next season.",
			}),
	},
	{
		source: "Be careful about losing too much money.",
		translate: () =>
			i18n.t("ownerMessage.loseMoneyWarning", {
				ns: "ui",
				defaultValue: "Be careful about losing too much money.",
			}),
	},
	{
		source: "Another season like that and you're fired!",
		translate: () =>
			i18n.t("ownerMessage.oneMoreBadSeason", {
				ns: "ui",
				defaultValue: "Another season like that and you're fired!",
			}),
	},
	{
		source: "A couple more seasons like that and you're fired!",
		translate: () =>
			i18n.t("ownerMessage.coupleMoreBadSeasons", {
				ns: "ui",
				defaultValue: "A couple more seasons like that and you're fired!",
			}),
	},
	{
		source:
			'You paid the luxury tax with the "You\'re fired if you pay the luxury tax" challenge mode enabled!',
		translate: () =>
			i18n.t("ownerMessage.luxuryTaxChallengeFired", {
				ns: "ui",
				defaultValue:
					'You paid the luxury tax with the "You\'re fired if you pay the luxury tax" challenge mode enabled!',
			}),
	},
	{
		source:
			'You missed the playoffs with the "You\'re fired if you miss the playoffs" challenge mode enabled!',
		translate: () =>
			i18n.t("ownerMessage.missPlayoffsChallengeFired", {
				ns: "ui",
				defaultValue:
					'You missed the playoffs with the "You\'re fired if you miss the playoffs" challenge mode enabled!',
			}),
	},
	{
		source: "You've been an all-around disappointment. You're fired.",
		translate: () =>
			i18n.t("ownerMessage.allAroundDisappointment", {
				ns: "ui",
				defaultValue: "You've been an all-around disappointment. You're fired.",
			}),
	},
	{
		source:
			"You've won some games, but you're just not making me enough profit. It's not all about wins and losses, dollars matter too. You're fired.",
		translate: () =>
			i18n.t("ownerMessage.notEnoughProfitFired", {
				ns: "ui",
				defaultValue:
					"You've won some games, but you're just not making me enough profit. It's not all about wins and losses, dollars matter too. You're fired.",
			}),
	},
	{
		source:
			"I like that you've made a nice profit for me, but you're not putting a competitive team on the court. We need a new direction. You're fired.",
		translate: () =>
			i18n.t("ownerMessage.notCompetitiveFired", {
				ns: "ui",
				defaultValue:
					"I like that you've made a nice profit for me, but you're not putting a competitive team on the court. We need a new direction. You're fired.",
			}),
	},
	{
		source: "You're fired.",
		translate: () =>
			i18n.t("ownerMessage.fired", {
				ns: "ui",
				defaultValue: "You're fired.",
			}),
	},
	{
		source:
			"I hear a few other teams are looking for a new GM, but your run here is over. Please, go run one of those teams into the ground.",
		translate: () =>
			i18n.t("ownerMessage.firedClosing", {
				ns: "ui",
				defaultValue:
					"I hear a few other teams are looking for a new GM, but your run here is over. Please, go run one of those teams into the ground.",
			}),
	},
	// N4-0 season expectation letter fragments.
	{
		source: "I want us contending for the title this year.",
		translate: () =>
			i18n.t("ownerMessage.expectationPlayoffContend", {
				ns: "ui",
				defaultValue: "I want us contending for the title this year.",
			}),
	},
	{
		source: "Get us back to the playoffs.",
		translate: () =>
			i18n.t("ownerMessage.expectationPlayoffMakePlayoffs", {
				ns: "ui",
				defaultValue: "Get us back to the playoffs.",
			}),
	},
	{
		source:
			"This is a building year — develop our young players and set us up for the future.",
		translate: () =>
			i18n.t("ownerMessage.expectationPlayoffDevelop", {
				ns: "ui",
				defaultValue:
					"This is a building year — develop our young players and set us up for the future.",
			}),
	},
	{
		source: "keep us in the black — don't lose money this year.",
		translate: () =>
			i18n.t("ownerMessage.expectationFinancesBody", {
				ns: "ui",
				defaultValue: "keep us in the black — don't lose money this year.",
			}),
	},
	{
		source:
			"You're still new here, so I won't be too hard on you yet. But keep these goals in mind.",
		translate: () =>
			i18n.t("ownerMessage.expectationClosingGrace", {
				ns: "ui",
				defaultValue:
					"You're still new here, so I won't be too hard on you yet. But keep these goals in mind.",
			}),
	},
	{
		source: "My patience is wearing thin. I need to see real results this season.",
		translate: () =>
			i18n.t("ownerMessage.expectationClosingWarning", {
				ns: "ui",
				defaultValue:
					"My patience is wearing thin. I need to see real results this season.",
			}),
	},
	{
		source: "Hit these targets and we'll get along just fine.",
		translate: () =>
			i18n.t("ownerMessage.expectationClosingNeutral", {
				ns: "ui",
				defaultValue: "Hit these targets and we'll get along just fine.",
			}),
	},
	// N4-3 monthly pulse verdicts.
	{
		source: "You're ahead of pace — keep it up.",
		translate: () =>
			i18n.t("ownerMessage.pulseVerdictAhead", {
				ns: "ui",
				defaultValue: "You're ahead of pace — keep it up.",
			}),
	},
	{
		source: "You're right on pace. Stay the course.",
		translate: () =>
			i18n.t("ownerMessage.pulseVerdictOnTrack", {
				ns: "ui",
				defaultValue: "You're right on pace. Stay the course.",
			}),
	},
	{
		source: "You're behind pace. I need to see a turnaround.",
		translate: () =>
			i18n.t("ownerMessage.pulseVerdictBehind", {
				ns: "ui",
				defaultValue: "You're behind pace. I need to see a turnaround.",
			}),
	},
	// N4-3 playoff clinch milestone.
	{
		source: "We've clinched a playoff spot.",
		translate: () =>
			i18n.t("ownerMessage.milestoneClinchedBase", {
				ns: "ui",
				defaultValue: "We've clinched a playoff spot.",
			}),
	},
	{
		source:
			"That's the floor for a team with our talent — now go make a deep run.",
		translate: () =>
			i18n.t("ownerMessage.milestoneClinchedContend", {
				ns: "ui",
				defaultValue:
					"That's the floor for a team with our talent — now go make a deep run.",
			}),
	},
	{
		source: "That's exactly what I asked for. Nicely done.",
		translate: () =>
			i18n.t("ownerMessage.milestoneClinchedMakePlayoffs", {
				ns: "ui",
				defaultValue: "That's exactly what I asked for. Nicely done.",
			}),
	},
	{
		source: "Ahead of schedule for a rebuilding year. Impressive.",
		translate: () =>
			i18n.t("ownerMessage.milestoneClinchedDevelop", {
				ns: "ui",
				defaultValue: "Ahead of schedule for a rebuilding year. Impressive.",
			}),
	},
	// N4-3 playoff elimination milestone.
	{
		source: "We've been eliminated from playoff contention.",
		translate: () =>
			i18n.t("ownerMessage.milestoneEliminatedBase", {
				ns: "ui",
				defaultValue: "We've been eliminated from playoff contention.",
			}),
	},
	{
		source:
			"This is a real disappointment. We were built to contend, not to miss the playoffs.",
		translate: () =>
			i18n.t("ownerMessage.milestoneEliminatedContend", {
				ns: "ui",
				defaultValue:
					"This is a real disappointment. We were built to contend, not to miss the playoffs.",
			}),
	},
	{
		source: "I expected us in the playoffs. We'll need to regroup.",
		translate: () =>
			i18n.t("ownerMessage.milestoneEliminatedMakePlayoffs", {
				ns: "ui",
				defaultValue: "I expected us in the playoffs. We'll need to regroup.",
			}),
	},
	{
		source:
			"I know this was a building year, so I won't overreact. Keep developing our young core.",
		translate: () =>
			i18n.t("ownerMessage.milestoneEliminatedDevelop", {
				ns: "ui",
				defaultValue:
					"I know this was a building year, so I won't overreact. Keep developing our young core.",
			}),
	},
] as const;

const translateExactFragments = (value: string) => {
	let translated = value;

	for (const { source, translate } of [...exactTranslations].sort(
		(a, b) => b.source.length - a.source.length,
	)) {
		translated = translated.replaceAll(source, translate());
	}

	if (i18n.language === "zh") {
		translated = translated.replace(/([。！？])\s+/g, "$1");
	}

	return translated;
};

const translateParagraph = (paragraph: string) => {
	const thisYear = paragraph.match(/^This year: (.+)$/);
	if (thisYear) {
		return i18n.t("ownerMessage.thisYear", {
			ns: "ui",
			defaultValue: "This year: {{mood}}",
			mood: translateMood(thisYear[1]!),
		});
	}

	const overall = paragraph.match(/^Overall: (.+)$/);
	if (overall) {
		return i18n.t("ownerMessage.overall", {
			ns: "ui",
			defaultValue: "Overall: {{mood}}",
			mood: translateOverall(overall[1]!),
		});
	}

	// N4-0 expectation letter labeled lines. The numbers/detail stay dynamic so
	// zh templates can reorder them; the inner sentences go through the exact
	// fragment table.
	const welcome = paragraph.match(
		/^Welcome to the (\d+) season\. Here's what I expect from you\.$/,
	);
	if (welcome) {
		return i18n.t("ownerMessage.expectationWelcome", {
			ns: "ui",
			defaultValue:
				"Welcome to the {{season}} season. Here's what I expect from you.",
			season: welcome[1],
		});
	}

	const wins = paragraph.match(
		/^Wins: aim for at least (\d+) wins this season\.$/,
	);
	if (wins) {
		return i18n.t("ownerMessage.expectationWins", {
			ns: "ui",
			defaultValue: "Wins: aim for at least {{winTarget}} wins this season.",
			winTarget: wins[1],
		});
	}

	const playoffs = paragraph.match(/^Playoffs: (.+)$/);
	if (playoffs) {
		return i18n.t("ownerMessage.expectationPlayoffs", {
			ns: "ui",
			defaultValue: "Playoffs: {{detail}}",
			detail: translateExactFragments(playoffs[1]!),
		});
	}

	const finances = paragraph.match(/^Finances: (.+)$/);
	if (finances) {
		return i18n.t("ownerMessage.expectationFinances", {
			ns: "ui",
			defaultValue: "Finances: {{detail}}",
			detail: translateExactFragments(finances[1]!),
		});
	}

	// N4-3 monthly pulse labeled lines.
	const pulseRecord = paragraph.match(
		/^Through (\d+) games, you're (\d+)-(\d+)\.$/,
	);
	if (pulseRecord) {
		return i18n.t("ownerMessage.pulseRecord", {
			ns: "ui",
			defaultValue: "Through {{gp}} games, you're {{won}}-{{lost}}.",
			gp: pulseRecord[1],
			won: pulseRecord[2],
			lost: pulseRecord[3],
		});
	}

	const pulseTarget = paragraph.match(/^Target: (\d+) wins\.$/);
	if (pulseTarget) {
		return i18n.t("ownerMessage.pulseTarget", {
			ns: "ui",
			defaultValue: "Target: {{winTarget}} wins.",
			winTarget: pulseTarget[1],
		});
	}

	// N4-3 free-agency recap labeled lines.
	const faIntro = paragraph.match(
		/^Free agency is closed\. Here's where your roster stands for the (\d+) season\.$/,
	);
	if (faIntro) {
		return i18n.t("ownerMessage.faSummaryIntro", {
			ns: "ui",
			defaultValue:
				"Free agency is closed. Here's where your roster stands for the {{season}} season.",
			season: faIntro[1],
		});
	}

	const faRoster = paragraph.match(/^Roster: (\d+) players signed\.$/);
	if (faRoster) {
		return i18n.t("ownerMessage.faSummaryRoster", {
			ns: "ui",
			defaultValue: "Roster: {{count}} players signed.",
			count: faRoster[1],
		});
	}

	const faPayroll = paragraph.match(/^Payroll: \$([\d.]+)M\.$/);
	if (faPayroll) {
		return i18n.t("ownerMessage.faSummaryPayroll", {
			ns: "ui",
			defaultValue: "Payroll: ${{payroll}}M.",
			payroll: faPayroll[1],
		});
	}

	const faCapBelow = paragraph.match(/^Cap room: \$([\d.]+)M below the cap\.$/);
	if (faCapBelow) {
		return i18n.t("ownerMessage.faSummaryCapBelow", {
			ns: "ui",
			defaultValue: "Cap room: ${{capRoom}}M below the cap.",
			capRoom: faCapBelow[1],
		});
	}

	const faCapOver = paragraph.match(/^Cap room: \$([\d.]+)M over the cap\.$/);
	if (faCapOver) {
		return i18n.t("ownerMessage.faSummaryCapOver", {
			ns: "ui",
			defaultValue: "Cap room: ${{capRoom}}M over the cap.",
			capRoom: faCapOver[1],
		});
	}

	return translateExactFragments(paragraph);
};

export const translateMessageFrom = (from: string) =>
	from === ownerMessageFrom
		? i18n.t("ownerMessage.from", {
				ns: "ui",
				defaultValue: "The Owner",
			})
		: from;

export const translateMessageSubject = (subject: string | undefined) => {
	if (subject === annualPerformanceEvaluation) {
		return i18n.t("ownerMessage.annualPerformanceEvaluation", {
			ns: "ui",
			defaultValue: "Annual performance evaluation",
		});
	}

	if (subject === seasonExpectations) {
		return i18n.t("ownerMessage.seasonExpectations", {
			ns: "ui",
			defaultValue: "Season expectations",
		});
	}

	if (subject === pulseSubject) {
		return i18n.t("ownerMessage.pulseSubject", {
			ns: "ui",
			defaultValue: "Season update",
		});
	}

	if (subject === milestoneClinchedSubject) {
		return i18n.t("ownerMessage.milestoneClinchedSubject", {
			ns: "ui",
			defaultValue: "Playoff berth clinched",
		});
	}

	if (subject === milestoneEliminatedSubject) {
		return i18n.t("ownerMessage.milestoneEliminatedSubject", {
			ns: "ui",
			defaultValue: "Eliminated from playoff contention",
		});
	}

	if (subject === freeAgencySummarySubject) {
		return i18n.t("ownerMessage.freeAgencySummarySubject", {
			ns: "ui",
			defaultValue: "Free agency recap",
		});
	}

	return subject;
};

export const translateMessageHtml = (html: string) =>
	html.replace(/<p>(.*?)<\/p>/g, (_match, paragraph: string) => {
		return `<p>${translateParagraph(paragraph)}</p>`;
	});

export const translateMessagePlainText = (text: string) => {
	let translated = text
		.replace(
			/This year: (Horrible!|Bad\.|Pretty good\.|Good\.|Excellent!)/g,
			(_match, mood: string) =>
				i18n.t("ownerMessage.thisYear", {
					ns: "ui",
					defaultValue: "This year: {{mood}}",
					mood: translateMood(mood),
				}),
		)
		.replace(
			/Overall: (Horrible!|Bad\.|Pretty good\.|Good\.|Excellent!|It's too early to judge you\.)/g,
			(_match, mood: string) =>
				i18n.t("ownerMessage.overall", {
					ns: "ui",
					defaultValue: "Overall: {{mood}}",
					mood: translateOverall(mood),
				}),
		);

	return translateExactFragments(translated);
};
